import React, { useEffect, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { parseJson, toJsonText } from '../lib/emsportsPartner';

/**
 * EveryMatrix yönetim ekranının ortak parçaları.
 *
 * Parçalar panelin **kendi** bileşenlerinden türer (`admin-card`,
 * `admin-input`, `admin-btn-*`) ve yerleşimleri `emsports.css` içindedir;
 * ham renk ve ölçü yazılmaz. Panelin vurgu rengi kurulum başına değişir
 * (`--admin-theme-accent`), yani sabit bir mavi yeşil vurgulu bir kurulumda
 * yabancı durur (bkz. CLAUDE.md — "Giriş kutusu ölçüleri Tailwind'den değil
 * CSS'ten gelir").
 *
 * Ekran iki tür alan taşır: **biçimi sabit** olanlar (cashout marjı, webhook
 * adresi) ve **katmanlı** olanlar (risk yapılandırması, görünürlük kümesi,
 * tema değişkenleri, marka gövdesi). İkincisi için kutu kutu bir form yazmak
 * sağlayıcının şemasını panele kopyalamak olurdu: sağlayıcı yeni bir alan
 * eklediğinde panel onu tanımaz ve yönetici hub'a dönmek zorunda kalır.
 * Bu yüzden katmanlı gövdeler denetimli bir JSON alanında düzenlenir —
 * dokümanın kendi talimatı da budur: *"read it, change it and send it back
 * complete"*.
 */

export const Panel = ({ title, description, actions, children }) => (
  <section className="admin-card ems-panel">
    <header className="ems-panel-head">
      <div>
        <h3 className="admin-card-title">{title}</h3>
        {description && <p className="admin-card-hint">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </header>
    {children}
  </section>
);

export const Field = ({ label, hint, children }) => (
  <label className="ems-field">
    <span>{label}</span>
    {children}
    {hint && <span className="ems-field-hint">{hint}</span>}
  </label>
);

export const Toggle = ({ label, hint, checked, onChange, disabled }) => (
  <label className="ems-toggle">
    <input
      type="checkbox"
      checked={!!checked}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
    />
    <span>
      <b>{label}</b>
      {hint && <span>{hint}</span>}
    </span>
  </label>
);

export const Busy = ({ children = 'Yükleniyor…' }) => (
  <div className="ems-busy" aria-live="polite">
    <Loader2 size={14} className="animate-spin" /> {children}
  </div>
);

export const Empty = ({ children }) => (
  <p className="ems-empty">{children}</p>
);

export const Problem = ({ children }) => {
  if (!children) return null;
  return (
    // Hata sonradan belirir: ekran okuyucu onu duyurmalı.
    <p className="ems-note ems-note-bad" role="alert" aria-live="polite">
      <AlertTriangle size={14} aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
};

/**
 * Sağlayıcının "uygulamadım" listesi.
 *
 * Yazma cevabındaki `ignored_fields` boş değilse kayıt **eksik**
 * uygulanmıştır; sessiz geçmek yöneticiye kurduğunu sandığı bir limitle
 * devam ettirirdi.
 */
export const IgnoredFields = ({ fields }) => {
  if (!fields?.length) return null;
  return (
    <p className="ems-note ems-note-warn" style={{ marginTop: 10 }}>
      <AlertTriangle size={14} aria-hidden="true" />
      <span>
        <b>Uygulanmayan alanlar:</b> {fields.join(', ')} — sağlayıcı bu
        anahtarları tanımadı, yazım hatası olabilir. Tanınmayan alan sessizce
        atıldığı için kural <u>kurulmuş sayılmaz</u>.
      </span>
    </p>
  );
};

/**
 * Denetimli JSON alanı.
 *
 * Değer yalnızca **geçerli** olduğunda yukarı verilir: bozuk bir gövdeyi
 * sağlayıcıya göndermek, hatayı panelde değil sağlayıcının 422'sinde
 * görmek demektir.
 */
export const JsonEditor = ({ value, onChange, rows = 14, hint }) => {
  const [text, setText] = useState(() => toJsonText(value));
  const [error, setError] = useState('');

  useEffect(() => {
    setText(toJsonText(value));
    setError('');
  }, [value]);

  const handle = (next) => {
    setText(next);
    const parsed = parseJson(next, {});
    setError(parsed.error);
    if (!parsed.error) onChange(parsed.value);
  };

  return (
    <div className="space-y-2">
      <textarea
        className="admin-input ems-json"
        rows={rows}
        spellCheck={false}
        value={text}
        onChange={(event) => handle(event.target.value)}
      />
      {hint && !error && <p className="ems-field-hint">{hint}</p>}
      <Problem>{error}</Problem>
    </div>
  );
};

export default Panel;
